import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { UserIcon, PencilIcon, CheckIcon, XMarkIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import OTPInput from '../Auth/OTPInput';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';
import { validatePassword, PasswordPolicy, PasswordValidationResult } from '../../utils/passwordValidation';

/**
 * Account/Info tab: name, email, role, status, Change Password, Delete Account.
 * Used on Profile page as the first tab "Info" (moved from Settings/Account).
 */
const AccountInfoTab: React.FC = () => {
  const { t } = useTranslation('settings');
  const { t: tProfile } = useTranslation('profile');
  const { t: tForgotPassword } = useTranslation('forgotPassword');
  const { user, refreshUserData, logout } = useAuth();
  const [modeTooltipOpen, setModeTooltipOpen] = useState(false);

  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [isUpdatingName, setIsUpdatingName] = useState(false);

  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailStep, setEmailStep] = useState<'edit' | 'otp' | 'success'>('edit');
  const [emailOtpError, setEmailOtpError] = useState('');
  const [emailResendCooldown, setEmailResendCooldown] = useState(0);
  const [isEmailLoading, setIsEmailLoading] = useState(false);

  const [showPasswordWarningModal, setShowPasswordWarningModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordStep, setPasswordStep] = useState<'current' | 'otp' | 'new' | 'success'>('current');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordOtpError, setPasswordOtpError] = useState('');
  const [passwordResendCooldown, setPasswordResendCooldown] = useState(0);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy | null>(null);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidationResult | null>(null);
  const [isLoadingPasswordPolicy, setIsLoadingPasswordPolicy] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [backupKeysBeforeDelete, setBackupKeysBeforeDelete] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isEditingEmail) handleEmailCancel();
    };
    if (isEditingEmail) document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isEditingEmail]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showPasswordModal) {
        setShowPasswordModal(false);
        setPasswordStep('current');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError('');
      }
    };
    if (showPasswordModal) document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [showPasswordModal]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showDeleteModal) setShowDeleteModal(false);
    };
    if (showDeleteModal) document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [showDeleteModal]);

  useEffect(() => {
    if (newPassword && passwordPolicy) {
      const validation = validatePassword(newPassword, passwordPolicy, user?.email, user?.name, tForgotPassword);
      setPasswordValidation(validation);
    } else {
      setPasswordValidation(null);
    }
  }, [newPassword, passwordPolicy, user?.email, user?.name, tForgotPassword]);

  const handleNameSubmit = async () => {
    if (!newName.trim()) {
      toast.error(t('toast.nameEmpty'));
      return;
    }
    if (newName.trim() === user?.name) {
      toast.error(t('toast.nameMustDiffer'));
      return;
    }
    try {
      setIsUpdatingName(true);
      await apiService.updateName(newName.trim());
      await refreshUserData();
      toast.success(t('toast.nameUpdated'));
      setIsEditingName(false);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || t('toast.nameUpdateFailed');
      toast.error(errorMessage);
    } finally {
      setIsUpdatingName(false);
    }
  };

  const handleNameCancel = () => {
    setIsEditingName(false);
    setNewName('');
  };

  const handleEmailEdit = () => {
    setIsEditingEmail(true);
    setNewEmail(user?.email || '');
    setEmailStep('edit');
  };

  const handleEmailCancel = () => {
    setIsEditingEmail(false);
    setNewEmail('');
    setEmailStep('edit');
    setEmailOtpError('');
  };

  const handleEmailSubmit = async () => {
    if (!newEmail || newEmail.toLowerCase() === user?.email?.toLowerCase()) {
      toast.error(t('toast.emailEnterDifferent'));
      return;
    }
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(newEmail)) {
      toast.error(t('toast.emailEnterValid'));
      return;
    }
    try {
      setIsEmailLoading(true);
      await apiService.requestEmailUpdate(user?.email || '', newEmail);
      setEmailStep('otp');
      toast.success(t('toast.otpSentNewEmail'));
      setEmailResendCooldown(30);
      const interval = setInterval(() => {
        setEmailResendCooldown((prev) => (prev <= 1 ? (clearInterval(interval), 0) : prev - 1));
      }, 1000);
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.response?.data?.message || t('toast.networkError'));
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleEmailOtpSubmit = async (otp: string) => {
    try {
      setIsEmailLoading(true);
      setEmailOtpError('');
      await apiService.confirmEmailUpdate(user?.email || '', newEmail, otp);
      setEmailStep('success');
      toast.success(t('toast.emailUpdated'));
      await refreshUserData();
      setTimeout(() => {
        setIsEditingEmail(false);
        setEmailStep('edit');
      }, 2000);
    } catch (error: any) {
      setEmailOtpError(error.response?.data?.error || error.response?.data?.message || t('toast.networkError'));
      toast.error(error.response?.data?.error || error.response?.data?.message || t('toast.networkError'));
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleEmailResendOtp = async () => {
    if (emailResendCooldown > 0) return;
    try {
      setIsEmailLoading(true);
      await apiService.requestEmailUpdate(user?.email || '', newEmail);
      toast.success(t('toast.otpSentEmail'));
      setEmailResendCooldown(30);
      const interval = setInterval(() => {
        setEmailResendCooldown((prev) => (prev <= 1 ? (clearInterval(interval), 0) : prev - 1));
      }, 1000);
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.response?.data?.message || t('toast.networkError'));
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handlePasswordChange = () => setShowPasswordWarningModal(true);
  const handlePasswordWarningCancel = () => setShowPasswordWarningModal(false);

  const handlePasswordWarningConfirm = async () => {
    setShowPasswordWarningModal(false);
    setShowPasswordModal(true);
    setPasswordStep('current');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordOtpError('');
    setPasswordError('');
    setPasswordValidation(null);
    if (user?.email) {
      try {
        setIsLoadingPasswordPolicy(true);
        const policy = await apiService.getPasswordPolicy(user.email);
        setPasswordPolicy(policy);
      } catch {
        toast.error(t('toast.passwordRequirementsFailed'));
        setPasswordPolicy({
          min_length: 8,
          require_uppercase: false,
          require_lowercase: false,
          require_numbers: false,
          require_special_chars: false,
          prohibit_easy_to_guess: false,
          prohibit_sequential_numbers: false,
          max_sequential_numbers: 2,
          prohibit_repeated_characters: false,
          max_repeated_characters: 2,
          prohibit_common_passwords: false,
          prohibit_user_info: false,
          require_password_rotation: false,
          password_rotation_days: 90,
          password_expiration_notification_days: 7,
        });
      } finally {
        setIsLoadingPasswordPolicy(false);
      }
    }
  };

  const handlePasswordModalClose = () => {
    setShowPasswordModal(false);
    setPasswordStep('current');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordOtpError('');
    setPasswordError('');
    setPasswordValidation(null);
    setPasswordPolicy(null);
  };

  const startPasswordResendCooldown = () => {
    setPasswordResendCooldown(30);
    const interval = setInterval(() => {
      setPasswordResendCooldown((prev) => (prev <= 1 ? (clearInterval(interval), 0) : prev - 1));
    }, 1000);
  };

  const handleCurrentPasswordSubmit = async () => {
    if (!currentPassword) {
      setPasswordError(t('passwordErrors.enterCurrent'));
      return;
    }
    if (!newPassword || !confirmPassword) {
      setPasswordError(t('passwordErrors.fillBoth'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t('passwordErrors.doNotMatch'));
      return;
    }
    if (passwordPolicy) {
      const validation = validatePassword(newPassword, passwordPolicy, user?.email, user?.name, tForgotPassword);
      if (!validation.isValid) {
        setPasswordError(validation.errors[0] || t('passwordErrors.doesNotMeet'));
        return;
      }
    } else if (newPassword.length < 8) {
      setPasswordError(t('passwordErrors.minLength'));
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError(t('passwordErrors.mustDiffer'));
      return;
    }
    try {
      setIsPasswordLoading(true);
      setPasswordError('');
      await apiService.requestPasswordUpdate(user?.email || '', currentPassword, newPassword);
      setPasswordStep('otp');
      toast.success('OTP sent to your email address');
      startPasswordResendCooldown();
    } catch (error: any) {
      if (error.response?.status === 401) {
        setPasswordError(t('passwordErrors.currentIncorrect'));
        toast.error(t('toast.currentPasswordIncorrect'));
      } else {
        setPasswordError(error.response?.data?.error || error.response?.data?.message || t('toast.networkError'));
        toast.error(error.response?.data?.error || error.response?.data?.message || t('toast.networkError'));
      }
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const handlePasswordOtpSubmit = async (otp: string) => {
    try {
      setIsPasswordLoading(true);
      setPasswordOtpError('');
      await apiService.confirmPasswordUpdate(user?.email || '', otp);
      setPasswordStep('success');
      toast.success(t('toast.passwordUpdated'));
      try {
        await apiService.revokeAllOtherSessions();
        await logout();
      } catch {
        // continue to redirect
      }
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
    } catch (error: any) {
      setPasswordOtpError(error.response?.data?.error || error.response?.data?.message || t('toast.networkError'));
      toast.error(error.response?.data?.error || error.response?.data?.message || t('toast.networkError'));
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const handlePasswordResendOtp = async () => {
    if (passwordResendCooldown > 0) return;
    try {
      setIsPasswordLoading(true);
      await apiService.requestPasswordUpdate(user?.email || '', currentPassword, newPassword);
      toast.success(t('toast.otpSentToEmail'));
      startPasswordResendCooldown();
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.response?.data?.message || t('toast.networkError'));
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const handleDeleteAccount = () => {
    setBackupKeysBeforeDelete(false);
    setShowDeleteModal(true);
  };
  const confirmDeleteAccount = async () => {
    if (!user?.id) return;
    try {
      setIsDeletingAccount(true);
      if (backupKeysBeforeDelete && user.key_backup_on_delete_allowed) {
        await apiService.getKeyExport();
      }
      await apiService.deleteUser(user.id);
      toast.success(t('toast.deleteAccountSuccess'));
      setShowDeleteModal(false);
      await logout();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || t('toast.deleteAccountFailed');
      toast.error(msg);
    } finally {
      setIsDeletingAccount(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <div className="bg-white dark:bg-black shadow overflow-hidden sm:rounded-lg border border-gray-200 dark:border-gray-900 transition-colors">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
            <UserIcon className="h-5 w-5 mr-2" />
            {t('account.accountInformation')}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
            {t('account.accountInformationDescription')}
          </p>
        </div>
        <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200 dark:sm:divide-gray-800">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('account.name')}</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2 flex items-center">
                {!isEditingName ? (
                  <div className="flex items-center">
                    <span>{user.name}</span>
                    <button
                      onClick={() => {
                        setIsEditingName(true);
                        setNewName(user.name || '');
                      }}
                      className="ml-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2 w-full">
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !isUpdatingName && handleNameSubmit()}
                      className="flex-1 px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500"
                      placeholder={t('account.placeholderName')}
                      autoFocus
                      disabled={isUpdatingName}
                    />
                    <button
                      onClick={handleNameSubmit}
                      disabled={isUpdatingName || !newName.trim()}
                      className="p-1 text-governate-600 hover:text-governate-700 dark:text-governate-400 dark:hover:text-governate-300 transition-colors disabled:opacity-50"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={handleNameCancel}
                      disabled={isUpdatingName}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('account.email')}</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2 flex items-center">
                {!isEditingEmail ? (
                  <div className="flex items-center">
                    <span>{user.email}</span>
                    <button
                      onClick={handleEmailEdit}
                      className="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : emailStep === 'edit' ? (
                  <div className="flex items-center space-x-2 w-full">
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleEmailSubmit()}
                      className="flex-1 px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500"
                      placeholder={t('account.placeholderNewEmail')}
                      autoFocus
                    />
                    <button
                      onClick={handleEmailSubmit}
                      disabled={isEmailLoading}
                      className="p-1 text-governate-600 hover:text-governate-700 transition-colors disabled:opacity-50"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button onClick={handleEmailCancel} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : emailStep === 'otp' ? (
                  <div className="w-full">
                    <div className="text-sm text-gray-600 mb-2">
                      {t('accountEmail.enterCodeSentTo', { email: newEmail })}
                    </div>
                    <OTPInput onComplete={handleEmailOtpSubmit} disabled={isEmailLoading} error={emailOtpError} />
                    <div className="mt-3 flex items-center justify-between">
                      <button
                        onClick={handleEmailResendOtp}
                        disabled={isEmailLoading || emailResendCooldown > 0}
                        className="text-sm text-governate-600 hover:text-governate-700 disabled:text-gray-400"
                      >
                        {isEmailLoading
                          ? t('accountEmail.sending')
                          : emailResendCooldown > 0
                            ? t('accountEmail.resendIn', { seconds: emailResendCooldown })
                            : t('accountEmail.resendCode')}
                      </button>
                      <button onClick={handleEmailCancel} className="text-sm text-gray-600 hover:text-gray-800">
                        {t('accountEmail.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="w-full">
                    <div className="text-sm text-green-600">{t('accountEmail.emailUpdatedSuccess')}</div>
                  </div>
                )}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('account.role')}</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.role === 'sysadmin'
                      ? 'bg-purple-100 text-purple-800'
                      : user.role === 'admin'
                        ? 'bg-blue-100 text-blue-800'
                        : user.role === 'user'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {user.role === 'sysadmin'
                    ? t('account.systemAdministrator')
                    : user.role === 'admin'
                      ? t('account.companyAdministrator')
                      : t('account.user')}
                </span>
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                {t('account.mode')}
                <span
                  className="relative inline-flex"
                  onMouseEnter={() => setModeTooltipOpen(true)}
                  onMouseLeave={() => setModeTooltipOpen(false)}
                >
                  <InformationCircleIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-help" aria-hidden />
                  {modeTooltipOpen && (
                    <div className="absolute left-0 top-full mt-1 z-10 w-72 p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg text-left">
                      {user?.mode === 'reader' ? (
                        <>
                          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{tProfile('modeTooltip.reader')}</p>
                          <ul className="space-y-1 text-sm list-disc list-inside">
                            <li className="text-red-600 dark:text-red-400">{tProfile('modeTooltip.readerCannot1')}</li>
                            <li className="text-red-600 dark:text-red-400">{tProfile('modeTooltip.readerCannot2')}</li>
                          </ul>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">{tProfile('modeTooltip.fullTitle')}</p>
                          <ul className="space-y-1 text-sm list-disc list-inside">
                            <li className="text-green-600 dark:text-green-400">{tProfile('modeTooltip.fullBullet1')}</li>
                            <li className="text-green-600 dark:text-green-400">{tProfile('modeTooltip.fullBullet2')}</li>
                            <li className="text-green-600 dark:text-green-400">{tProfile('modeTooltip.fullBullet3')}</li>
                            <li className="text-green-600 dark:text-green-400">{tProfile('modeTooltip.fullBullet4')}</li>
                          </ul>
                        </>
                      )}
                    </div>
                  )}
                </span>
              </dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2 flex items-center gap-3 flex-wrap">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user?.mode === 'reader' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                  }`}
                >
                  {user?.mode === 'reader' ? tProfile('mode.reader') : tProfile('mode.full')}
                </span>
                {user?.mode === 'reader' && (
                  <button
                    type="button"
                    disabled
                    className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
                  >
                    {t('account.upgradeToFull')}
                  </button>
                )}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('account.accountStatus')}</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.is_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {user.is_verified ? t('account.verified') : t('account.pendingVerification')}
                </span>
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:px-6 border-t border-gray-200 dark:border-gray-800">
              <div className="flex flex-col space-y-3 max-w-md">
                <button
                  onClick={handlePasswordChange}
                  className="w-full px-4 py-2 rounded-lg text-sm font-medium border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  {t('account.changePassword')}
                </button>
                <button
                  onClick={handleDeleteAccount}
                  className="w-full px-4 py-2 rounded-lg text-sm font-medium border-2 border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 bg-white dark:bg-gray-900 hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
                >
                  {t('account.deleteAccount')}
                </button>
              </div>
            </div>
          </dl>
        </div>
      </div>

      {/* Password Warning Modal */}
      {showPasswordWarningModal &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="p-5 border w-96 rounded-md bg-white dark:bg-gray-800">
              <div className="mt-3 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full border-2 border-yellow-600">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="mt-4 px-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('modals.passwordWarning.title')}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{t('modals.passwordWarning.description')}</p>
                </div>
                <div className="flex space-x-3 mt-6 px-4">
                  <button
                    onClick={handlePasswordWarningCancel}
                    className="flex-1 px-3 py-2 rounded-lg text-sm font-medium border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800"
                  >
                    {t('modals.passwordWarning.cancel')}
                  </button>
                  <button
                    onClick={handlePasswordWarningConfirm}
                    className="flex-1 px-3 py-2 rounded-lg text-sm font-medium border-2 border-green-500 dark:border-green-600 text-white bg-green-600 hover:bg-green-700"
                  >
                    {t('modals.passwordWarning.continue')}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Password Change Modal - abbreviated for length; full modal content from Settings */}
      {showPasswordModal &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="p-5 border w-96 rounded-md bg-white dark:bg-gray-800 max-h-[90vh] overflow-y-auto">
              <div className="mt-3">
                {passwordStep === 'current' && (
                  <>
                    <div className="text-center mb-4">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('modals.changePassword.title')}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{t('modals.changePassword.enterCurrentAndNew')}</p>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('modals.changePassword.currentPassword')}
                        </label>
                        <input
                          type="password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                          placeholder={t('modals.changePassword.placeholderCurrent')}
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('modals.changePassword.newPassword')}
                        </label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white ${
                            newPassword.length > 0 && passwordValidation && !passwordValidation.isValid
                              ? 'border-red-300 dark:border-red-600'
                              : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder={passwordPolicy ? t('modals.changePassword.placeholderNewMin', { min: passwordPolicy.min_length }) : t('modals.changePassword.placeholderNew')}
                          disabled={isLoadingPasswordPolicy}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('modals.changePassword.confirmNewPassword')}
                        </label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleCurrentPasswordSubmit()}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                          placeholder={t('modals.changePassword.confirmNewPassword')}
                        />
                      </div>
                      {passwordError && <div className="text-sm text-red-600">{passwordError}</div>}
                      <div className="flex space-x-3">
                        <button
                          onClick={handlePasswordModalClose}
                          className="flex-1 px-3 py-2 rounded-lg text-sm font-medium border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                        >
                          {t('modals.changePassword.cancel')}
                        </button>
                        <button
                          onClick={handleCurrentPasswordSubmit}
                          disabled={
                            isPasswordLoading ||
                            !currentPassword ||
                            !newPassword ||
                            !confirmPassword ||
                            (passwordValidation && !passwordValidation.isValid) ||
                            isLoadingPasswordPolicy
                          }
                          className="flex-1 px-3 py-2 rounded-lg text-sm font-medium border-2 border-green-500 text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                        >
                          {isPasswordLoading ? t('modals.changePassword.sendingOtp') : isLoadingPasswordPolicy ? t('modals.changePassword.loading') : t('modals.changePassword.continue')}
                        </button>
                      </div>
                    </div>
                  </>
                )}
                {passwordStep === 'otp' && (
                  <>
                    <div className="text-center mb-4">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('modals.verifyIdentity.title')}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        {t('modals.verifyIdentity.enterCodeSentTo', { email: user?.email })}
                      </p>
                    </div>
                    <OTPInput onComplete={handlePasswordOtpSubmit} disabled={isPasswordLoading} error={passwordOtpError} />
                    <div className="text-center mt-3">
                      <button
                        onClick={handlePasswordResendOtp}
                        disabled={isPasswordLoading || passwordResendCooldown > 0}
                        className="text-sm text-governate-600 hover:text-governate-700 disabled:text-gray-400"
                      >
                        {passwordResendCooldown > 0 ? t('modals.verifyIdentity.resendIn', { seconds: passwordResendCooldown }) : t('modals.verifyIdentity.resendCode')}
                      </button>
                    </div>
                    <div className="flex space-x-3 mt-4">
                      <button onClick={() => setPasswordStep('current')} className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-md">
                        {t('modals.verifyIdentity.back')}
                      </button>
                      <button onClick={handlePasswordModalClose} className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-md">
                        {t('modals.verifyIdentity.cancel')}
                      </button>
                    </div>
                  </>
                )}
                {passwordStep === 'success' && (
                  <div className="text-center">
                    <CheckIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t('modals.passwordUpdated.title')}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('modals.passwordUpdated.description')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Delete Account Modal */}
      {showDeleteModal &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="p-5 border w-96 rounded-md bg-white dark:bg-gray-800">
              <div className="mt-3 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full border-2 border-red-600">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-2 px-7 py-3">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('modals.deleteAccount.title')}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{t('modals.deleteAccount.description')}</p>
                  {user?.key_backup_on_delete_allowed && (
                    <div className="flex items-center gap-2 mt-3">
                      <input
                        type="checkbox"
                        id="backup-keys-before-delete"
                        checked={backupKeysBeforeDelete}
                        onChange={(e) => setBackupKeysBeforeDelete(e.target.checked)}
                        className="rounded border-gray-300 text-governate-600 focus:ring-governate-500"
                      />
                      <label htmlFor="backup-keys-before-delete" className="text-sm text-gray-700 dark:text-gray-300">
                        {t('modals.deleteAccount.backupKeysBeforeDelete')}
                      </label>
                    </div>
                  )}
                </div>
                <div className="flex justify-center gap-2 px-4 py-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    disabled={isDeletingAccount}
                    className="px-4 py-2 rounded-lg text-sm font-medium border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50"
                  >
                    {t('modals.deleteAccount.cancel')}
                  </button>
                  <button
                    onClick={confirmDeleteAccount}
                    disabled={isDeletingAccount}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white border-2 border-red-600 hover:bg-red-700 disabled:opacity-50"
                  >
                    {isDeletingAccount ? t('modals.deleteAccount.deleting') : t('modals.deleteAccount.delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default AccountInfoTab;
